import { devLog } from "./dev-log";

const FAILURE_COUNT_KEY = "import-reloader-failure-count";
const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 1000; // 1 second
const MAX_BACKOFF_MS = 8000; // 8 seconds

/**
 * Gets the current failure count from sessionStorage
 */
function getFailureCount(): number {
  try {
    const count = sessionStorage.getItem(FAILURE_COUNT_KEY);
    return count ? Number.parseInt(count, 10) : 0;
  } catch {
    return 0;
  }
}

/**
 * Increments the failure count in sessionStorage
 */
function incrementFailureCount(): number {
  try {
    const current = getFailureCount();
    const newCount = current + 1;
    sessionStorage.setItem(FAILURE_COUNT_KEY, String(newCount));
    return newCount;
  } catch {
    return 1;
  }
}

/**
 * Resets the failure count in sessionStorage
 */
function resetFailureCount(): void {
  try {
    sessionStorage.removeItem(FAILURE_COUNT_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Calculates exponential backoff delay with jitter
 */
function calculateBackoff(attempt: number): number {
  const baseDelay = Math.min(
    INITIAL_BACKOFF_MS * 2 ** (attempt - 1),
    MAX_BACKOFF_MS,
  );
  // Add jitter (±25%) to prevent thundering herd
  const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1);
  return Math.max(0, baseDelay + jitter);
}

/**
 * Delays execution for the specified milliseconds
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// biome-ignore lint/suspicious/noExplicitAny: we don't care about the type
async function importOrReload(dynamicImportFn: () => Promise<any>) {
  const failureCount = getFailureCount();

  try {
    const result = await dynamicImportFn();
    // Reset failure count on successful import
    if (failureCount > 0) {
      resetFailureCount();
    }
    return result;
  } catch (error) {
    const newFailureCount = incrementFailureCount();

    // Log the error for debugging
    console.error(
      `[importOrReload] Failed to load module (attempt ${newFailureCount}/${MAX_RETRIES}):`,
      error,
    );

    // If we've exceeded max retries, reload the page
    if (newFailureCount >= MAX_RETRIES) {
      console.error(
        `[importOrReload] Max retries (${MAX_RETRIES}) exceeded. Reloading page...`,
      );
      resetFailureCount(); // Reset before reload to prevent immediate loop
      window.location.reload();
      throw error;
    }

    // Calculate backoff delay and retry
    const backoffMs = calculateBackoff(newFailureCount);
    devLog(`[importOrReload] Retrying in ${Math.round(backoffMs)}ms...`);

    await delay(backoffMs);

    // Recursively retry
    return importOrReload(dynamicImportFn);
  }
}

export { importOrReload };
